// src/queries/order.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@services/api";

const base = "/order";

export const orderKeys = {
  list: (filters, page, size) => ["orders", { filters, page, size }],
  one: (id) => ["order", id],
  products: (id) => ["order", id, "products"],
  notes: (id) => ["order", id, "notes"],
  history: (id) => ["order", id, "history"],
  totals: (id) => ["order", id, "totals"],

  detailsFilter: (filters = {}) => ["order-details-filter", filters],
  statuses: (lang = 1) => ["order-statuses", lang],

  einvoiceSend: () => ["order-einvoice-send"],
  einvoiceSendOne: (id) => ["order", id, "einvoice-send"],
};

/* ===== LIST (already in your app) ===== */
export function useOrdersQuery(filters, page = 1, pageSize = 100) {
  const params = new URLSearchParams();
  if (filters?.orderId) params.set("order_id", filters.orderId);
  if (filters?.customerId) params.set("customer_id", filters.customerId);
  if (filters?.invoiceNo) params.set("invoice_no", filters.invoiceNo);
  if (filters?.orderType) params.set("type", filters.orderType);
  if (filters?.orderStatus) params.set("status", filters.orderStatus);
  if (filters?.storeId) params.set("store_id", filters.storeId);
  if (filters?.unpaidOnly) params.set("unpaid", "1");
  if (filters?.rescheduleOnly) params.set("reschedule", "1");
  if (filters?.firstTimeOnly) params.set("first_time", "1");
  if (filters?.outSourceOnly) params.set("out_source", "1");
  if (filters?.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters?.dateTo) params.set("date_to", filters.dateTo);
  params.set("sort", filters?.sort || "-date_added");
  params.set("page", page);
  params.set("page_size", pageSize);

  return useQuery({
    queryKey: orderKeys.list(filters, page, pageSize),
    queryFn: async () => {
      const { data } = await api.get(`${base}?${params.toString()}`);
      return Array.isArray(data)
        ? { data, meta: { total: data.length } }
        : data;
    },
    keepPreviousData: true,
    staleTime: 30_000,
  });
}

/* ===== ONE ORDER ===== */
export function useOrderQuery(id) {
  return useQuery({
    enabled: !!id,
    queryKey: orderKeys.one(id),
    queryFn: async () => (await api.get(`${base}/${id}`)).data,
    staleTime: 15_000,
  });
}

const buildDetailsParams = (f = {}) => {
  const p = new URLSearchParams();
  if (f.order_ids) {
    p.set("order_ids", Array.isArray(f.order_ids) ? f.order_ids.join(",") : String(f.order_ids));
  }
  if (f.order_type) p.set("order_type", f.order_type);
  if (f.franchise_id !== undefined && f.franchise_id !== null && f.franchise_id !== "") {
    p.set("franchise_id", String(f.franchise_id));
  }
  if (f.from_date) p.set("from_date", f.from_date);
  if (f.to_date) p.set("to_date", f.to_date || f.from_date || "");
  if (f.dropoff_date) p.set("dropoff_date", f.dropoff_date);
  if (f.time_slot) {
    if (Array.isArray(f.time_slot)) {
      for (const ts of f.time_slot) {
        if (ts === undefined || ts === null || ts === "") continue;
        p.append("time_slot[]", String(ts));
      }
    } else {
      p.set("time_slot", f.time_slot);
    }
  }

  if (f.order_status_id !== undefined && f.order_status_id !== null && f.order_status_id !== "") {
    const ids = Array.isArray(f.order_status_id) ? f.order_status_id : [f.order_status_id];
    for (const id of ids) {
      if (id === undefined || id === null || id === "") continue;
      p.append("order_status_id[]", String(id));
    }
  }

  if (f.with_items !== undefined) {
    const v =
      f.with_items === true ||
      f.with_items === 1 ||
      f.with_items === "1" ||
      f.with_items === "true";
    p.set("with_items", v ? "1" : "0");
  }

  if (f.flagged_items !== undefined) {
    const v =
      f.flagged_items === true ||
      f.flagged_items === 1 ||
      f.flagged_items === "1" ||
      f.flagged_items === "true";
    p.set("flagged_items", v ? "1" : "0");
  }

  if (f.sewing_items !== undefined) {
    const v =
      f.sewing_items === true ||
      f.sewing_items === 1 ||
      f.sewing_items === "1" ||
      f.sewing_items === "true";
    p.set("sewing_items", v ? "1" : "0");
  }

  if (f.packed !== undefined && f.packed !== null && f.packed !== "") {
    p.set("packed", String(f.packed));
  }

  p.set("page", f.page || 1);
  p.set("page_size", f.page_size || 100);
  return p.toString();
};

export function useOrderDetailsFilterQuery(filters, opts = {}) {
  // By default this endpoint opts OUT of the active-marketplace header: the
  // two-day cleaning/packing pages pass an explicit franchise_id (e.g. "all")
  // to gather WashyWash + FlashWash orders regardless of the viewer's
  // marketplace. Pages that should follow the super admin's selected
  // marketplace (e.g. Orders by Day — WN-0140) pass { respectMarketplace: true }.
  const respectMarketplace = !!opts.respectMarketplace;
  return useQuery({
    queryKey: [...orderKeys.detailsFilter(filters), respectMarketplace ? "mp" : "all-mp"],
    queryFn: async () => {
      const qs = buildDetailsParams(filters);
      const { data } = await api.get(`${base}/details/filter?${qs}`, {
        skipMarketplace: !respectMarketplace,
      });
      return data;
    },
    keepPreviousData: true,
    staleTime: 30_000,
  });
}

export function useOrderProductsQuery(id) {
  return useQuery({
    enabled: !!id,
    queryKey: orderKeys.products(id),
    queryFn: async () => (await api.get(`${base}/${id}/products`)).data,
  });
}

export function useOrderTotalsQuery(id) {
  return useQuery({
    enabled: !!id,
    queryKey: orderKeys.totals(id),
    queryFn: async () => (await api.get(`${base}/${id}/totals`)).data,
  });
}

export function useOrderHistoryQuery(id) {
  return useQuery({
    enabled: !!id,
    queryKey: orderKeys.history(id),
    queryFn: async () => (await api.get(`${base}/${id}/history`)).data,
  });
}

export function useSlotOrderCountQuery(date, timeSlot) {
  return useQuery({
    enabled: !!(date && timeSlot),
    queryKey: ["slot-order-count", date, timeSlot],
    queryFn: async () => {
      const p = new URLSearchParams({ date, time_slot: timeSlot });
      const { data } = await api.get(`${base}/slot/count?${p.toString()}`);
      return data?.count ?? 0;
    },
    staleTime: 60_000,
  });
}

/* ===== Mutations ===== */
const invalidate = (qc, id) => {
  qc.invalidateQueries({ queryKey: orderKeys.one(id) });
  qc.invalidateQueries({ queryKey: orderKeys.products(id) });
  qc.invalidateQueries({ queryKey: orderKeys.totals(id) });
};

export function useUpdateShipping(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shipping_method) =>
      api.patch(`${base}/${id}/shipping`, { shipping_method }),
    onSuccess: () => invalidate(qc, id),
  });
}

export function useAddProduct(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => {
      return api.post(`${base}/${id}/products`, {
        product_id: Number(payload.product_id),
        quantity: Number(payload.quantity),
      });
    },
    onSuccess: () => invalidate(qc, id),
  });
}

export function useUpdateProduct(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ order_product_id, ...patch }) =>
      api.patch(`${base}/${id}/products/${order_product_id}`, patch),
    onSuccess: () => invalidate(qc, id),
  });
}

export function useDeleteProduct(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (order_product_id) =>
      api.delete(`${base}/${id}/products/${order_product_id}`),
    onSuccess: () => invalidate(qc, id),
  });
}

export function useTogglePacked(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ order_product_id, packed, count }) =>
      api.patch(`${base}/${id}/products/${order_product_id}/packed`, {
        packed,
        count,
      }),
    onSuccess: () => invalidate(qc, id),
  });
}

export function useChangeStatus(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ status, comment, notify, override }) =>
      api.post(`${base}/${id}/status`, { status, comment, notify, override }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.one(id) });
      qc.invalidateQueries({ queryKey: orderKeys.history(id) });
    },
  });
}

export function useUpdateAddress(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, ...payload }) =>
      api.post(`/order/${id}/address`, { type, ...payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.one(id) });
    },
  });
}

export function useUpdateTimeSlot(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, time_slot }) =>
      api.post(`/order/${id}/time-slot`, { type, time_slot }),
    onSuccess: () => qc.invalidateQueries({ queryKey: orderKeys.one(id) }),
  });
}

export function useUpdateStore(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (store_id) => api.post(`/order/${id}/store`, { store_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: orderKeys.one(id) }),
  });
}

export function useProductsSearchQuery(q, limit = 20) {
  return useQuery({
    enabled: !!(q && q.trim().length >= 1),
    queryKey: ["products-search", q, limit],
    queryFn: async () => {
      const { data } = await api.get("/products/search", {
        params: { q, limit },
      });
      const rows = Array.isArray(data?.data) ? data.data : [];
      return rows.map((p) => ({
        value: p.product_id ?? p.id,
        label: p.name || p.model || p.sku || `#${p.product_id ?? p.id}`,
        model: p.model,
        price: p.price != null ? Number(p.price) : null,
        raw: p,
      }));
    },
    staleTime: 30_000,
  });
}

/* ===== Notes ===== */
export function useOrderNotesQuery(id) {
  return useQuery({
    enabled: !!id,
    queryKey: orderKeys.notes(id),
    queryFn: async () => (await api.get(`${base}/${id}/notes`)).data,
    staleTime: 15_000,
  });
}

export function useAddNote(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ comment }) => api.post(`${base}/${id}/notes`, { comment }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.notes(id) });
    },
  });
}

/* ===== Utility: fetch all statuses ===== */
export function useOrderStatusesQuery(langId = 1) {
  return useQuery({
    queryKey: ["order-statuses", langId],
    queryFn: async () => {
      const { data } = await api.get(`${base}/statuses/list?lang=${langId}`);
      return (data || []).sort(
        (a, b) =>
          (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
          a.name.localeCompare(b.name)
      );
    },
    staleTime: 60_000,
  });
}

export function useSetCoupon(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (coupon) => api.post(`${base}/${id}/coupon`, { coupon }),
    onSuccess: () => invalidate(qc, id),
  });
}

export function useSetDamage(orderId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ order_product_id, payload }) =>
      api.post(`${base}/${orderId}/products/${order_product_id}/damage`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.products(orderId) });
    },
  });
}

export function useSetSorry(orderId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ order_product_id, payload }) =>
      api.post(`${base}/${orderId}/products/${order_product_id}/sorry`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.products(orderId) });
    },
  });
}

export function usePack(orderId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/order/${orderId}/pack`);
      return data;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: orderKeys.one(orderId) });
      const prev = qc.getQueryData(orderKeys.one(orderId));
      qc.setQueryData(orderKeys.one(orderId), (old) => ({ ...(old || prev || {}), packed: 1, start_pack: 0 }));
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(orderKeys.one(orderId), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: orderKeys.one(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.products(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.totals(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.history(orderId) });
      qc.invalidateQueries({ queryKey: orderKeys.notes(orderId) });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["order-details-filter"] });
    },
  });
}

/* ===== JoFotara e-invoice (bulk) =====
   Success shape:
   {
     no_tax, select_all, order_ids, counts, results: [{ order_id, api_status: "PASS"|"FAIL", ... }]
   }
*/
export function useSendEinvoiceBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: orderKeys.einvoiceSend(),
    mutationFn: async ({ selected, select_all = false, no_tax = false, filters = undefined }) => {
      const payload = {
        selected: (selected || []).map((x) => Number(x)),
        select_all: Boolean(select_all),
        no_tax: Boolean(no_tax),
      };
      if (filters !== undefined) payload.filters = filters;
      const { data } = await api.post(`/orders/einvoice/send`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useSendEinvoiceOne(orderId) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: orderKeys.einvoiceSendOne(orderId),
    mutationFn: async ({ no_tax = false } = {}) => {
      const { data } = await api.post(`/orders/${orderId}/einvoice/send`, {
        no_tax: Boolean(no_tax),
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.one(orderId) });
    },
  });
}

